import { useState } from 'react';
import { rsme, type Asset } from '../lib/rsme';
import { useQuery } from '../ui/useQuery';
import { PageHead, Card, Pill, Input, Loading, ErrorState, EmptyState } from '../ui/primitives';

const STATUSES: Asset['status'][] = ['available', 'rented', 'maintenance', 'sold', 'reserved'];

export default function Fleet() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<Asset['status'] | ''>('');
  const q = useQuery(() => rsme.assets.list({ search, status: status || undefined, limit: 100 }), [search, status]);

  return (
    <div className="max-w-6xl mx-auto">
      <PageHead title="Fleet" sub="Every asset, current state, current customer" />
      <div className="px-5 md:px-8 flex flex-wrap gap-2 items-center">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search asset, brand, model, tonnage" className="md:max-w-sm" />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as Asset['status'] | '')}
          className="h-10 px-3 rounded-lg bg-elevated border border-line text-sm"
        >
          <option value="">all statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="px-5 md:px-8 mt-4">
        {q.loading && <Loading rows={6} />}
        {q.error && <ErrorState error={q.error} retry={q.refetch} />}
        {!q.loading && !q.error && !q.data?.data?.length && (
          <EmptyState title="No assets match" hint="Try clearing filters." />
        )}
        {q.data?.data && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {q.data.data.map((a) => <AssetCard key={a.id} a={a} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function AssetCard({ a }: { a: Asset }) {
  const tone = a.status === 'available' ? 'good' : a.status === 'rented' ? 'accent' : a.status === 'maintenance' ? 'warn' : 'muted';
  return (
    <Card className="!p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium truncate">{a.assetName}</div>
          <div className="text-xs text-muted truncate">{a.brand} {a.model} · {a.tonnage}T</div>
        </div>
        <Pill tone={tone}>{a.status}</Pill>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
        <div><div className="text-muted">LTA</div><div className="tabular-nums">{a.lta ?? '—'}</div></div>
        <div><div className="text-muted">Mast</div><div>{a.mastType ?? '—'}</div></div>
        <div><div className="text-muted">Entity</div><div>{a.entity ?? '—'}</div></div>
      </div>
    </Card>
  );
}
